import { CurrencyCode, GeocodeAddress, ProgramCreateParams } from '@citruscamps/citrus-client'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { MapboxGeocoder } from '../../../../../components/MapboxGeocoder/MapboxGeocoder'
import { Title } from '../../../../../components/Title/Title'
import translations from '../../../../../translations/en.json'
import { Address } from '../../../../../utils/address'
import { getCurrencyCodeFromAddress } from '../../../../../utils/currency'

interface IProps {
  initialValues?: ProgramCreateParams
  onPrevious?: (value: ProgramCreateParams) => Promise<void>
  onSubmit: (value: ProgramCreateParams) => Promise<void>
}

export const ProgramStep = ({
  initialValues,
  onPrevious,
  onSubmit,
}: IProps): React.ReactElement => {
  const [isCurrencySelectable, setIsCurrencySelectable] = useState(false)
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldError,
    setFieldValue,
    isSubmitting,
  } = useFormik<ProgramCreateParams>({
    initialValues: {
      ...(initialValues || {}),
      traffic_source: initialValues?.traffic_source
        ? {
            utm_source: initialValues?.traffic_source?.utm_source || 'direct',
            utm_medium: initialValues?.traffic_source?.utm_medium || 'none',
            utm_campaign: initialValues?.traffic_source?.utm_campaign || 'none',
            utm_term: initialValues?.traffic_source?.utm_term,
            utm_content: initialValues?.traffic_source?.utm_content,
          }
        : undefined,
      member_title: initialValues?.member_title || '',
      name: initialValues?.name || '',
      website: initialValues?.website || '',
      address: initialValues?.address!,
      types: initialValues?.types || [],
      tags: initialValues?.tags || [],
      language: initialValues?.language || navigator.language,
      timezone: initialValues?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validationSchema: Yup.object().shape({
      member_title: Yup.string().max(45, (translations as any)['error.title_maxlength']),
      name: Yup.string()
        .required((translations as any)['error.program_name_required'])
        .max(40, (translations as any)['error.program_name_maxlength']),
      address: Yup.object().required((translations as any)['error.address_required']),
      types: Yup.array().of(Yup.string()),
      tags: Yup.array().of(Yup.string()),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await onSubmit(value)
      } catch (e: any) {
        if (e.param) setFieldError(e.param, e.message)
      }
      setSubmitting(false)
    },
  })

  return (
    <form onSubmit={handleSubmit}>
      <Title value="Setup Program" />
      <h4 className="mb-4">Tell us a bit about yourself and your program.</h4>
      <div className="mb-3">
        <label>Your program name</label>
        <input
          id="name"
          name="name"
          autoComplete="organization"
          type="text"
          className={'form-control' + (touched.name && !!errors.name ? ' is-invalid' : '')}
          value={values?.name}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus
        />
        <div className="invalid-feedback d-block">
          {touched.name && !!errors.name ? errors.name : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label className="optional">Your title</label>
        <input
          id="member_title"
          name="member_title"
          autoComplete="organization-title"
          type="text"
          className={
            'form-control' + (touched.member_title && !!errors.member_title ? ' is-invalid' : '')
          }
          value={values?.member_title}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <div className="invalid-feedback d-block">
          {touched?.member_title && !!errors?.member_title ? errors?.member_title : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label className="optional">Your program website</label>
        <input
          id="website"
          name="website"
          autoComplete="url"
          type="text"
          className={'form-control' + (touched.website && !!errors.website ? ' is-invalid' : '')}
          value={values?.website}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <div className="invalid-feedback d-block">
          {touched?.website && !!errors?.website ? errors?.website : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label>Where is your program located?</label>
        <MapboxGeocoder
          inputName="address"
          types={['place']}
          result={values.address}
          proximity={(initialValues || values)?.address?.geometry?.coordinates}
          invalid={!!touched?.address && !!errors?.address}
          onSelect={(value?: GeocodeAddress) => {
            const country: string = (value && Address.fromGeocode(value).country_short_code) || 'CA'
            setFieldValue('language', country ? `en-${country}` : navigator.language)
            setFieldValue('address', value, true)
            setFieldValue('default_currency', value && getCurrencyCodeFromAddress(value), true)
          }}
        />
        <div className="invalid-feedback d-block">
          {touched.address && !!errors.address && typeof errors.address === 'string' ? (
            errors.address
          ) : values.address && values.default_currency && !isCurrencySelectable ? (
            <span className="text-body">
              Your currency will be in <b>{values.default_currency.toUpperCase()}</b>.{' '}
              <button
                className="btn btn-link btn-sm p-0"
                style={{ verticalAlign: 'baseline' }}
                onClick={() => setIsCurrencySelectable(true)}
              >
                Change currency
              </button>
            </span>
          ) : (
            '\u00A0'
          )}
        </div>
      </div>
      {isCurrencySelectable && (
        <div className="mb-3">
          <label>Your currency</label>
          <select
            name="default_currency"
            id="default_currency"
            className="form-select"
            defaultValue={values.default_currency}
            onChange={handleChange}
          >
            {Object.values(CurrencyCode).map((currency) => (
              <option value={currency} key={`option-${currency}`}>
                {currency.toUpperCase()}
              </option>
            ))}
          </select>
          <div className="invalid-feedback d-block">
            {touched?.default_currency && !!errors?.default_currency
              ? errors?.default_currency
              : '\u00A0'}
          </div>
        </div>
      )}
      <div className="d-flex justify-content-between mt-4">
        {onPrevious && (
          <button
            type="button"
            className="btn btn-link me-auto"
            disabled={isSubmitting}
            onClick={() => onPrevious(values)}
          >
            Back
          </button>
        )}
        <LoadingButton type="submit" className="btn btn-primary ms-auto" loading={isSubmitting}>
          Next
        </LoadingButton>
      </div>
    </form>
  )
}
